/** 从资源管理器拖入预览区时传递的资源元信息。 */
export type CanvasResourceDropPayload = {
  categoryKey: 'graphic' | 'audio' | 'font' | 'video'
  folderPath: string
  relativePath: string
  mappingPath: string
  resourceUrl: string
  name: string
  ext: string
  source?: 'native' | 'touch'
}

export type CanvasResourceDragPayload = CanvasResourceDropPayload & {
  source: 'native' | 'touch'
}

export const CANVAS_IMAGE_DROP_COLUMNS = 5
export const CANVAS_IMAGE_DROP_ROWS = 5
export const CANVAS_IMAGE_DROP_ZONE_COUNT = 23
export const CANVAS_AUDIO_DROP_COLUMNS = 4
export const CANVAS_AUDIO_DROP_ZONE_COUNT = 4

function buildImageTrackColor(index: number) {
  const t = Math.max(0, Math.min(1, index / Math.max(1, CANVAS_IMAGE_DROP_ZONE_COUNT - 4)))
  const hue = 210 - Math.round(t * 160)
  const saturation = 58 + Math.round(t * 10)
  const lightness = 42 + Math.round(t * 8)
  const borderLightness = Math.min(78, lightness + 18)
  return {
    color: `hsl(${hue} ${saturation}% ${lightness}%)`,
    borderColor: `hsl(${hue} ${Math.max(46, saturation - 8)}% ${borderLightness}%)`,
  }
}

/** 图像拖入画布时可选择的事件创建位置。 */
export const imageDropZones = Array.from({ length: CANVAS_IMAGE_DROP_ZONE_COUNT }, (_, index) => {
  if (index === 0) {
    return { label: '1 显示远景图', color: '#2563eb', borderColor: '#60a5fa' }
  }
  if (index === 1) {
    return { label: '2 显示背景图', color: '#0891b2', borderColor: '#22d3ee' }
  }
  if (index === CANVAS_IMAGE_DROP_ZONE_COUNT - 1) {
    return { label: `${CANVAS_IMAGE_DROP_ZONE_COUNT} 显示前景图`, color: '#7c3aed', borderColor: '#a78bfa' }
  }
  const trackColor = buildImageTrackColor(index - 2)
  return {
    label: `${index + 1} 显示图片`,
    color: trackColor.color,
    borderColor: trackColor.borderColor,
  }
})

/** 图像/按钮 目录资源拖入预览区时的落点（整屏 1 格，创建图片选项）。 */
export const buttonDropZones = [
  { label: '创建图片选项', color: '#7c3aed', borderColor: '#a78bfa' },
] as const

/** 音频拖入画布时可选择的音轨。 */
export const audioDropZones = [
  { label: '音轨 1', color: '#3b82f6', borderColor: '#60a5fa' },
  { label: '音轨 2', color: '#10b981', borderColor: '#34d399' },
  { label: '音轨 3', color: '#f59e0b', borderColor: '#fbbf24' },
  { label: '音轨 4', color: '#ef4444', borderColor: '#f87171' },
] as const

/** 图像资源在资源管理器中的顶层目录名（如 `按钮`、`背景`）。 */
export function getCanvasDropTopFolder(payload: CanvasResourceDropPayload): string {
  return payload.folderPath.split('/').filter(Boolean)[0] ?? ''
}

/** 图像/按钮 目录下的资源：拖入预览区应创建图片选项（ButtonDif），而非显示图片。 */
export function isButtonGraphicDrop(payload: CanvasResourceDropPayload): boolean {
  return payload.categoryKey === 'graphic' && getCanvasDropTopFolder(payload) === '按钮'
}

/** 按资源目录判断音频资源是否能拖入画布创建事件。 */
export function canCreateAudioEventFromDrop(payload: CanvasResourceDragPayload): boolean {
  const topFolder = getCanvasDropTopFolder(payload)
  return topFolder === '音乐' || topFolder === '音效' || topFolder === '语音' || topFolder === '循环音效'
}

/** 清洗资源拖拽 payload，避免 UI 组件重复处理空值。 */
export function normalizeCanvasDropItem(payload: Partial<CanvasResourceDropPayload> | null | undefined): CanvasResourceDropPayload | null {
  if (!payload?.relativePath || !payload.categoryKey) return null
  return {
    categoryKey: payload.categoryKey as CanvasResourceDropPayload['categoryKey'],
    folderPath: payload.folderPath?.trim() ?? '',
    relativePath: payload.relativePath.trim(),
    mappingPath: payload.mappingPath?.trim() ?? '',
    resourceUrl: payload.resourceUrl?.trim() ?? '',
    name: payload.name?.trim() ?? '',
    ext: payload.ext?.trim() ?? '',
    source: payload.source === 'touch' ? 'touch' : 'native',
  }
}
